import React, { useRef, useEffect, useState } from 'react';
import { themes } from '../data/themes';
import { Check, Sparkles, Palette } from 'lucide-react';

/**
 * ThemeGallery - Grid of deck themes for template selection
 * Renders mini previews of title and content slides for each theme
 */
export function ThemeGallery({ selectedTheme, onSelectTheme, brandGuidelines }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="h-5 w-5 text-purple-500" />
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          Choose a Theme
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {themes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isSelected={selectedTheme?.id === theme.id}
            onSelect={() => onSelectTheme(theme)}
            brandGuidelines={brandGuidelines}
          />
        ))}
      </div>

      {selectedTheme && (
        <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="text-sm text-purple-700 dark:text-purple-300">
              <strong>{selectedTheme.name}</strong> selected - Your slides will be generated instantly!
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Individual theme card with mini canvas previews
 */
function ThemeCard({ theme, isSelected, onSelect, brandGuidelines }) {
  const titleCanvasRef = useRef(null);
  const contentCanvasRef = useRef(null);
  const [previewsRendered, setPreviewsRendered] = useState(false);

  // Render mini previews when component mounts
  useEffect(() => {
    renderPreviews();
  }, [theme, brandGuidelines]);

  const renderPreviews = async () => {
    const titleCanvas = titleCanvasRef.current;
    const contentCanvas = contentCanvasRef.current;

    if (!titleCanvas || !contentCanvas) return;

    // Render title slide preview
    renderMiniPreview(titleCanvas, theme.templates.title, theme.colorScheme, {
      title: 'Presentation Title',
      content: 'Subtitle goes here',
    });

    // Render content/bullets slide preview
    renderMiniPreview(contentCanvas, theme.templates.bullets, theme.colorScheme, {
      title: 'Key Points',
      bullet_points: ['First point', 'Second point', 'Third point'],
    });

    setPreviewsRendered(true);
  };

  return (
    <div
      onClick={onSelect}
      className={`
        relative cursor-pointer rounded-xl p-3 transition-all duration-200
        ${isSelected
          ? 'bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-500 shadow-lg'
          : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md'
        }
      `}
    >
      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center shadow-md z-10">
          <Check className="h-4 w-4 text-white" />
        </div>
      )}

      {/* Theme name and description */}
      <div className="mb-3">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
          {theme.name}
        </h4>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {theme.description}
        </p>
      </div>

      {/* Mini slide previews */}
      <div className="flex gap-2">
        <div className="flex-1">
          <canvas
            ref={titleCanvasRef}
            width={160}
            height={90}
            className="w-full rounded-md shadow-sm"
          />
          <p className="text-[10px] text-slate-400 mt-1 text-center">Title</p>
        </div>
        <div className="flex-1">
          <canvas
            ref={contentCanvasRef}
            width={160}
            height={90}
            className="w-full rounded-md shadow-sm"
          />
          <p className="text-[10px] text-slate-400 mt-1 text-center">Content</p>
        </div>
      </div>

      {/* Color palette preview */}
      <div className="flex gap-1 mt-3 justify-center">
        <div
          className="w-4 h-4 rounded-full shadow-sm"
          style={{ backgroundColor: theme.colorScheme.primary }}
          title="Primary"
        />
        <div
          className="w-4 h-4 rounded-full shadow-sm"
          style={{ backgroundColor: theme.colorScheme.secondary }}
          title="Secondary"
        />
        <div
          className="w-4 h-4 rounded-full shadow-sm"
          style={{ backgroundColor: theme.colorScheme.accent }}
          title="Accent"
        />
        <div
          className="w-4 h-4 rounded-full shadow-sm border border-slate-300"
          style={{ backgroundColor: theme.colorScheme.background }}
          title="Background"
        />
      </div>
    </div>
  );
}

/**
 * Render a mini preview of a template on a small canvas
 */
function renderMiniPreview(canvas, template, colorScheme, sampleContent) {
  const ctx = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Draw background
  const bg = template.layout.background;
  if (bg.type === 'gradient') {
    // Parse CSS gradient and create canvas gradient
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    // Extract colors from gradient string (simplified)
    const colors = extractGradientColors(bg.value);
    colors.forEach((color, i) => {
      gradient.addColorStop(i / Math.max(colors.length - 1, 1), color);
    });
    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = bg.value || colorScheme.background;
  }
  ctx.fillRect(0, 0, width, height);

  // Draw elements at mini scale
  template.layout.elements.forEach((element) => {
    const x = (element.position.x / 100) * width;
    const y = (element.position.y / 100) * height;
    const w = (element.position.width / 100) * width;
    const h = (element.position.height / 100) * height;

    if (element.type === 'shape') {
      ctx.fillStyle = element.style.backgroundColor || colorScheme.primary;
      const radius = element.style.borderRadius || 0;
      roundRect(ctx, x, y, w, h, radius);
      ctx.fill();
    } else if (element.type === 'title' || element.type === 'subtitle' || element.type === 'content') {
      const content = sampleContent[element.placeholder] || '';
      if (content) {
        ctx.fillStyle = element.style.color || colorScheme.text;
        const fontSize = Math.max((element.style.fontSize / 100) * height * 1.2, 6);
        ctx.font = `${element.style.fontWeight || '400'} ${fontSize}px ${element.style.fontFamily?.split(',')[0] || 'sans-serif'}`;
        ctx.textAlign = element.style.textAlign || 'left';
        ctx.textBaseline = 'top';

        const textX = element.style.textAlign === 'center' ? x + w / 2 :
                      element.style.textAlign === 'right' ? x + w : x;

        // Truncate text for mini preview
        const displayText = typeof content === 'string' ? content.substring(0, 30) : '';
        ctx.fillText(displayText, textX, y);
      }
    } else if (element.type === 'bullets') {
      const bullets = sampleContent[element.placeholder] || [];
      if (bullets.length > 0) {
        ctx.fillStyle = element.style.color || colorScheme.textMuted;
        const fontSize = Math.max((element.style.fontSize / 100) * height * 1.2, 5);
        ctx.font = `${element.style.fontWeight || '400'} ${fontSize}px ${element.style.fontFamily?.split(',')[0] || 'sans-serif'}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const lineHeight = fontSize * 1.8;
        bullets.slice(0, 3).forEach((bullet, i) => {
          // Draw bullet point
          ctx.fillStyle = element.style.bulletColor || colorScheme.primary;
          ctx.beginPath();
          ctx.arc(x + 3, y + i * lineHeight + fontSize / 2, 2, 0, Math.PI * 2);
          ctx.fill();

          // Draw text
          ctx.fillStyle = element.style.color || colorScheme.textMuted;
          ctx.fillText(bullet.substring(0, 20), x + 8, y + i * lineHeight);
        });
      }
    }
  });
}

/**
 * Extract colors from a CSS gradient string
 */
function extractGradientColors(gradientStr) {
  const colorRegex = /#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}|rgba?\([^)]+\)/g;
  const matches = gradientStr.match(colorRegex) || [];
  return matches.length > 0 ? matches : ['#1a1a2e', '#16213e'];
}

/**
 * Draw a rounded rectangle
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

export default ThemeGallery;
